import React from 'react';

import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { normalizeProfileLike } from 'utils/profileLikeUtils';
import { TextComponent } from 'design/text/textComponents';
import { SidebarModalSectionComponent } from 'design/sidebarModal';
import { MarginComponent } from 'design/margin';
import {
  SmallTableRowComponent,
  SmallTableWrapperComponent,
} from 'design/smallTable';
import { useCardPaymentManagerDataProcess } from 'hooks/useCardPaymentManagerDataProcess';
import { useAppState } from 'hooks/useAppState';
import { ClickControlComponent } from 'design/clickControl/clickControlComponents';

type Props = {
  cardPaymentId: number;
  onShowClientDetails(clientId: number): void;
};

export const CardPaymentDetailsSummary = (props: Props) => {
  const { cardPaymentId, onShowClientDetails } = props;

  const {
    cardPaymentManager: {
      data: { cardPayment },
    },
  } = useAppState();

  useCardPaymentManagerDataProcess(cardPaymentId);

  const intl = useIntlDictionary();

  const { cardPaymentDetails: ownDictionary, shared: sharedDictionary } = intl;

  const statusText = cardPayment.status
    ? intl.shared.cardPaymentStatusValue(cardPayment.status)
    : '-';

  return (
    <>
      <SidebarModalSectionComponent
        theme="head"
        content={
          <TextComponent size="mpp" weight="medium">
            {ownDictionary.titleText(cardPayment.id)}
          </TextComponent>
        }
        actions={null}
        navigation={null}
      />
      <SidebarModalSectionComponent
        content={
          <>
            <TextComponent size="mp" weight="medium">
              {ownDictionary.detailsTable.titleText}
            </TextComponent>
            <MarginComponent top={20} />
            <SmallTableWrapperComponent>
              <SmallTableRowComponent>
                <TextComponent>
                  {ownDictionary.detailsTable.recievedAmountLabel}
                </TextComponent>
                <TextComponent>
                  {cardPayment.creditedTransaction
                    ? sharedDictionary.paymentTransactionText(
                        cardPayment.creditedTransaction
                      )
                    : null}
                </TextComponent>
              </SmallTableRowComponent>
              <SmallTableRowComponent>
                <TextComponent>
                  {ownDictionary.detailsTable.creditedAmountLabel}
                </TextComponent>
                <TextComponent>
                  {cardPayment.status !== 'REVERSED' &&
                  cardPayment.status !== 'DECLINED'
                    ? sharedDictionary.paymentTransactionText(
                        cardPayment.recievedTransaction
                      )
                    : '-'}
                </TextComponent>
              </SmallTableRowComponent>
              <SmallTableRowComponent>
                <TextComponent>
                  {ownDictionary.detailsTable.exchangeRateLabel}
                </TextComponent>
                <TextComponent>
                  {cardPayment.exchangeRate &&
                  cardPayment.status !== 'REVERSED' &&
                  cardPayment.status !== 'DECLINED'
                    ? sharedDictionary.exhangeRateText(cardPayment.exchangeRate)
                    : '-'}
                </TextComponent>
              </SmallTableRowComponent>
              <SmallTableRowComponent>
                <TextComponent>
                  {ownDictionary.detailsTable.statusLabel}
                </TextComponent>
                <TextComponent
                  color={
                    cardPayment.status === 'DECLINED'
                      ? 'critical'
                      : cardPayment.status === 'REVERSED'
                      ? 'secondary'
                      : 'primary'
                  }
                  size="m"
                >
                  {statusText}
                </TextComponent>
              </SmallTableRowComponent>
              {cardPayment.status === 'DEPOSITED' && (
                <SmallTableRowComponent>
                  <TextComponent>
                    {ownDictionary.detailsTable.orderIdLabel}
                  </TextComponent>
                  <TextComponent>{cardPayment.orderId}</TextComponent>
                </SmallTableRowComponent>
              )}
              {cardPayment.status === 'DEPOSITED' && (
                <SmallTableRowComponent>
                  <TextComponent>
                    {ownDictionary.detailsTable.descriptionLabel}
                  </TextComponent>
                  <TextComponent>{cardPayment.description}</TextComponent>
                </SmallTableRowComponent>
              )}
              <SmallTableRowComponent>
                <TextComponent>
                  {ownDictionary.detailsTable.updatedAtLabel}
                </TextComponent>
                <TextComponent>
                  {cardPayment.updatedAt
                    ? sharedDictionary.dates.calendarDateWithTime(
                        new Date(cardPayment.updatedAt)
                      )
                    : '-'}
                </TextComponent>
              </SmallTableRowComponent>
              <SmallTableRowComponent>
                <TextComponent>
                  {ownDictionary.detailsTable.createdAtLabel}
                </TextComponent>
                <TextComponent>
                  {sharedDictionary.dates.calendarDateWithTime(
                    new Date(cardPayment.createdAt)
                  )}
                </TextComponent>
              </SmallTableRowComponent>
              <SmallTableRowComponent>
                <TextComponent>
                  {ownDictionary.detailsTable.idLabel}
                </TextComponent>
                <TextComponent>{cardPayment.id}</TextComponent>
              </SmallTableRowComponent>
            </SmallTableWrapperComponent>
          </>
        }
        actions={null}
      />
      <SidebarModalSectionComponent
        content={
          <>
            <TextComponent size="mp" weight="medium">
              {ownDictionary.clientsTable.titleText}
            </TextComponent>
            <MarginComponent top={20} />
            <SmallTableWrapperComponent>
              <SmallTableRowComponent>
                <TextComponent>
                  {ownDictionary.clientsTable.organizationLabel}
                </TextComponent>
                <TextComponent>
                  <ClickControlComponent
                    onClick={() => onShowClientDetails(cardPayment.userId)}
                  >
                    <TextComponent color="accent">
                      {cardPayment.userProfile.company}
                    </TextComponent>
                  </ClickControlComponent>
                </TextComponent>
              </SmallTableRowComponent>
              <SmallTableRowComponent>
                <TextComponent>
                  {ownDictionary.clientsTable.fullNameLabel}
                </TextComponent>
                <TextComponent>
                  {normalizeProfileLike(cardPayment.userProfile).toFullName()}
                </TextComponent>
              </SmallTableRowComponent>
            </SmallTableWrapperComponent>
          </>
        }
        actions={null}
      />
    </>
  );
};
